import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { compareSemVer, parseSemVer } from "../../../utils/semver";
import type { InAppAnnouncementBanner, InAppAnnouncementBannerDoc, VersionFilter } from "./settingsTypes";

// Last app version without version filter support. These apps read only the legacy banner fields.
export const LAST_LEGACY_APP_VERSION = "1.7.5";

export type LegacyAppsMatch = "all" | "some" | "none";

export const getLegacyAppsMatch = (filter: VersionFilter | null): LegacyAppsMatch => {
  if (!filter) {
    return "all";
  }
  const target = parseSemVer(filter.version);
  const lastLegacy = parseSemVer(LAST_LEGACY_APP_VERSION);
  if (!target || !lastLegacy) {
    return "some";
  }
  const result = compareSemVer(target, lastLegacy);
  switch (filter.operator) {
    case "lte":
      return result >= 0 ? "all" : "some";
    case "eq":
    case "gte":
      return result > 0 ? "none" : "some";
  }
};

export class InAppAnnouncementBannerService {
  private static instance: InAppAnnouncementBannerService;
  private readonly COLLECTION = 'metadata';
  private readonly DOC_ID = 'in_app_announcement_banner';

  private constructor() {}

  public static getInstance(): InAppAnnouncementBannerService {
    if (!InAppAnnouncementBannerService.instance) {
      InAppAnnouncementBannerService.instance = new InAppAnnouncementBannerService();
    }
    return InAppAnnouncementBannerService.instance;
  }

  public async getInAppAnnouncementBanner(): Promise<InAppAnnouncementBanner> {
    const docRef = doc(db, this.COLLECTION, this.DOC_ID);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data() as InAppAnnouncementBannerDoc;
      if (data.banner) {
        return {
          bannerText: data.banner.text,
          bannerType: data.banner.type,
          showEveryTime: data.banner.showEveryTime,
          versionFilter: data.banner.versionFilter,
          showOnLegacyApps: data.banner.showOnLegacyApps,
        };
      }
      return {
        bannerText: data.bannerText,
        bannerType: data.bannerType,
        showEveryTime: data.showEveryTime,
        versionFilter: null,
        showOnLegacyApps: true,
      };
    }
    return {
      bannerText: "",
      bannerType: "info",
      showEveryTime: false,
      versionFilter: null,
      showOnLegacyApps: true,
    };
  }

  public async updateInAppAnnouncementBanner(banner: InAppAnnouncementBanner): Promise<void> {
    const docRef = doc(db, this.COLLECTION, this.DOC_ID);
    const legacyAppsMatch = getLegacyAppsMatch(banner.versionFilter);
    const showOnLegacyApps = legacyAppsMatch === "all" || (legacyAppsMatch === "some" && banner.showOnLegacyApps);
    const newBanner = {
        bannerText: showOnLegacyApps ? banner.bannerText : "",
        bannerType: banner.bannerType,
        showEveryTime: banner.showEveryTime,
        banner: {
            text: banner.bannerText,
            type: banner.bannerType,
            showEveryTime: banner.showEveryTime,
            versionFilter: banner.versionFilter,
            showOnLegacyApps: banner.showOnLegacyApps,
        },
        updatedAt: serverTimestamp(),
    }
    await setDoc(docRef, newBanner, { merge: true });
  }
}

export const inAppAnnouncementBannerService = InAppAnnouncementBannerService.getInstance();
